import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Database, ArrowRight, Check, Zap, MapPin, Layers, ShieldCheck, Sparkles, Phone, Download } from "lucide-react";
import { Button } from "../components/common/UIKit";
import { getLeadStats } from "../api/stats";
import { getSettings } from "../api/settings";
import { useAuthStore } from "../store/authStore";
import { useSiteName } from "../hooks/useSiteName";
import { computeBundlePrice, formatPrice, readPricing, getLeadTypePrice } from "../utils/pricing";

import { getBillingPeriod } from "../api/subscriptions";

const LEAD_TYPE_LABELS = {
  PROBATE: "Probate",
  DIVORCE: "Divorce",
  NOD: "Notice of Default",
  TAXLIEN: "Tax Liens",
  TAX_LIENS: "Tax Liens",
  EXPIRED: "Expired",
  CANCELED: "Canceled",
  WITHDRAWN: "Withdrawn",
  FSBO: "FSBO",
};

const PLAN_LEAD_TYPES = [
  { key: "fsbo", label: "FSBO" },
  { key: "probate", label: "Probate" },
  { key: "nod", label: "NOD" },
  { key: "tax_liens", label: "Tax Liens" },
  { key: "divorce", label: "Divorce" },
  { key: "expired", label: "Expired" },
  { key: "canceled", label: "Canceled" },
  { key: "withdrawn", label: "Withdrawn" },
];



function formatNumber(n) {
  return Number(n || 0).toLocaleString("en-US");
}

function Navbar() {
  const token = useAuthStore((s) => s.token);
  const siteName = useSiteName();
  const [isScrolled, setIsScrolled] = useState(false);
  
  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToPricing = (e) => {
    e.preventDefault();
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  
  return (
    <header className={`w-full sticky top-0 z-30 transition-all duration-300 ${
      isScrolled 
        ? 'border-b border-zinc-200/50 bg-white/95 backdrop-blur-xl shadow-sm' 
        : 'border-b border-transparent bg-white/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group" data-testid="landing-logo">
          <div className="h-8 w-8 sm:h-9 sm:w-9 bg-gradient-to-br from-zinc-900 to-zinc-700 rounded-lg flex items-center justify-center shadow-lg shadow-zinc-900/10 group-hover:shadow-zinc-900/20 transition-all duration-300 group-hover:scale-105">
            <Database className="h-4 w-4 sm:h-4.5 sm:w-4.5 text-white" strokeWidth={2.5} />
          </div>
          <span
            className="text-base sm:text-lg font-black tracking-tight text-zinc-900 truncate"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="landing-site-name"
          >
            {siteName}
          </span>
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <a href="#pricing" onClick={scrollToPricing} data-testid="landing-nav-pricing" className="hidden sm:block">
            <Button variant="ghost" className="h-9 px-4 hover:bg-zinc-100 transition-all duration-200">Pricing</Button>
          </a>
          {token ? (
            <Link to="/dashboard" data-testid="landing-nav-dashboard">
              <Button variant="primary" className="h-8 sm:h-9 px-3 sm:px-5 text-xs sm:text-sm bg-zinc-900 hover:bg-zinc-800 transition-all duration-200 hover:shadow-lg hover:shadow-zinc-900/10">
                Dashboard <ArrowRight className="h-3.5 w-3.5 ml-1 sm:ml-1.5" />
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-nav-login">
                <Button variant="ghost" className="h-8 sm:h-9 px-2 sm:px-4 text-xs sm:text-sm hover:bg-zinc-100 transition-all duration-200">Log in</Button>
              </Link>
              <Link to="/signup" data-testid="landing-nav-signup">
                <Button variant="primary" className="h-8 sm:h-9 px-3 sm:px-5 text-xs sm:text-sm bg-zinc-900 hover:bg-zinc-800 transition-all duration-200 hover:shadow-lg hover:shadow-zinc-900/10">
                  Get started
                </Button>
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  const token = useAuthStore((s) => s.token);
  return (
    <section className="relative overflow-hidden" data-testid="landing-hero">
      {/* Soft gradient wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] bg-gradient-to-br from-indigo-100 via-white to-white rounded-full blur-3xl opacity-80" />
        <div className="absolute -bottom-56 -left-40 w-[36rem] h-[36rem] bg-gradient-to-tr from-emerald-50 via-white to-white rounded-full blur-3xl opacity-80" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 lg:pt-20 pb-12 sm:pb-20 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Left: copy + CTA */}
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-zinc-700 bg-white border border-zinc-200 rounded-full px-3 sm:px-4 py-1 sm:py-1.5 mb-5 sm:mb-6 shadow-sm">
            {/* <Zap className="h-3.5 w-3.5 text-amber-500" fill="currentColor" /> */}
            Fresh skip-traced leads, every day
          </span>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-zinc-950 leading-[0.95] mb-5 sm:mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="landing-hero-title"
          >
            The control room for<br />
            <span className="bg-gradient-to-r from-zinc-950 to-zinc-600 bg-clip-text text-transparent">
              real estate leads.
            </span>
          </h1>

          <p className="text-base sm:text-lg text-zinc-600 max-w-xl mb-6 sm:mb-8 leading-relaxed">
            Subscribe to the counties you farm. Target the lead types you close.
            Skip the spreadsheets and work a live, deduplicated pipeline of FSBO,
            Probate, NOD, Tax-Lien, Divorce &amp; more.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-6 sm:mb-8">
            {token ? (
              <Link to="/subscriptions" data-testid="landing-hero-cta">
                <Button variant="primary" className="h-11 sm:h-12 px-5 sm:px-7 text-sm sm:text-base bg-zinc-950 hover:bg-zinc-800 rounded-xl">
                  Go to dashboard <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/signup" data-testid="landing-hero-cta">
                  <Button variant="primary" className="h-11 sm:h-12 px-5 sm:px-7 text-sm sm:text-base bg-zinc-950 hover:bg-zinc-800 rounded-xl">
                    Explore features <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button variant="outline" className="h-11 sm:h-12 px-5 sm:px-7 text-sm sm:text-base border-2 border-zinc-300 hover:border-zinc-900 rounded-xl">
                    See pricing
                  </Button>
                </a>
              </>
            )}
          </div>

          {/* Trust row */}
          <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2 text-[11px] sm:text-xs font-semibold text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" strokeWidth={3} /> Skip-traced</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" strokeWidth={3} /> Deduped daily</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" strokeWidth={3} /> Cancel anytime</span>
            <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-green-600" strokeWidth={3} /> No contracts</span>
          </div>
        </div>

        {/* Right: illustrative mock card stack */}
        <div className="lg:col-span-5 relative hidden lg:block">
          <HeroLeadCardStack />
        </div>
      </div>
    </section>
  );
}

function HeroLeadCardStack() {
  const rows = [
    { label: "Probate", color: "bg-purple-50 text-purple-700 ring-purple-200", name: "Robert Johnson", addr: "2204 Bayshore Blvd, Tampa FL", phone: "(813) 555-0128" },
    { label: "FSBO", color: "bg-rose-50 text-rose-700 ring-rose-200", name: "Maria Alvarez", addr: "1588 Coral Way, Miami FL", phone: "(305) 555-0042" },
    { label: "Divorce", color: "bg-amber-50 text-amber-700 ring-amber-200", name: "J. Lee & K. Lee", addr: "908 Ocean Ave, Brooklyn NY", phone: "(718) 555-0011" },
    { label: "Tax Lien", color: "bg-emerald-50 text-emerald-700 ring-emerald-200", name: "Carlos Rivera", addr: "411 Elm St, Austin TX", phone: "(512) 555-0199" },
  ];
  return (
    <div className="relative" data-testid="hero-lead-stack">
      {/* Decorative tilted background card */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-zinc-900 to-zinc-700 rotate-3 opacity-10" />
      <div className="relative rounded-3xl bg-white border border-zinc-200 shadow-2xl shadow-zinc-900/10 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-zinc-950 flex items-center justify-center">
              <Database className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-600">Today's leads</p>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-700 bg-green-50 ring-1 ring-green-200 rounded px-2 py-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" /> Live
          </span>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-zinc-100 bg-zinc-50/60 p-3 hover:bg-white hover:border-zinc-200 transition-colors">
              <span className={`text-[10px] font-bold uppercase tracking-widest rounded ring-1 px-2 py-0.5 ${r.color}`}>{r.label}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-zinc-950 truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{r.name}</p>
                <p className="text-xs text-zinc-500 truncate">{r.addr}</p>
              </div>
              <span className="text-xs font-mono font-bold text-zinc-700 hidden sm:inline">{r.phone}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-3 text-center">
          <div><p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Skip-traced</p><p className="text-xl font-black text-zinc-950 tabular-nums" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>98%</p></div>
          <div><p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Counties</p><p className="text-xl font-black text-zinc-950 tabular-nums" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>3,007</p></div>
          <div><p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Lead types</p><p className="text-xl font-black text-zinc-950 tabular-nums" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>8</p></div>
        </div>
      </div>
    </div>
  );
}

function StatsSection() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["lead-stats"],
    queryFn: getLeadStats,
    staleTime: 60_000,
  });

  const items = data?.leads_by_type || [];
  const total = items.reduce((s, i) => s + (i.count || 0), 0);

  return (
    <section className="border-y border-zinc-200/50 bg-gradient-to-b from-zinc-50 to-white" data-testid="landing-stats">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center mb-8 sm:mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Live Inventory</p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900 mb-3 sm:mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {isLoading ? (
              <span className="inline-block w-32 h-12 bg-zinc-200 animate-pulse rounded-lg"></span>
            ) : (
              <span className="bg-gradient-to-r from-zinc-900 to-zinc-600 bg-clip-text text-transparent">
                {formatNumber(total)}
              </span>
            )}{" "}
            leads ready to work
          </h2>
          <p className="text-base text-zinc-500">
            Fresh inventory across every lead type we cover.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {isLoading && Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-32 bg-gradient-to-br from-zinc-100 to-zinc-50 border border-zinc-200/50 rounded-xl animate-pulse"
              data-testid={`stats-skeleton-${i}`}
            />
          ))}
          {!isLoading && isError && (
            <p className="col-span-full text-center text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
              Could not load live stats. Please refresh.
            </p>
          )}
          {!isLoading && !isError && items.map((item) => {
            const label = LEAD_TYPE_LABELS[item.source_model] || item.source_model;
            return (
              <div
                key={item.source_model}
                className="group bg-white border border-zinc-200/80 rounded-xl p-6 hover:border-zinc-400 hover:shadow-xl hover:shadow-zinc-200/50 transition-all duration-300 hover:-translate-y-1"
                data-testid={`stats-card-${item.source_model}`}
              >
                <p
                  className="text-4xl font-black tracking-tight text-zinc-900 mb-2 group-hover:scale-105 transition-transform duration-300"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {formatNumber(item.count)}
                </p>
                <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
                  {label} Leads
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pricing({ settings, loading, lockedBilling }) {
  const pricing = useMemo(() => readPricing(settings || {}), [settings]);
  const { pricePerCounty, discountPercent, leadTypeCategories } = pricing;

  const categoryLines = useMemo(() => {
    return Object.entries(leadTypeCategories).map(([, cat]) => {
      const names = (cat.lead_types || []).map((k) => {
        const found = PLAN_LEAD_TYPES.find((p) => p.key === k);
        return found ? found.label : k;
      }).join(", ");
      return `${names} — ${formatPrice(cat.price)}/mo each`;
    });
  }, [leadTypeCategories]);

  return (
    <section id="pricing" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24" data-testid="landing-pricing">
      <div className="text-center mb-10 sm:mb-14">
        <div className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 rounded-full px-4 py-1.5 mb-5 sm:mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-600">Pricing</p>
        </div>
        <h2
          className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900 mb-3 sm:mb-4"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Build your plan. Pay only for what you farm.
        </h2>
        <div className="text-sm sm:text-base text-zinc-500 max-w-xl mx-auto space-y-1">
          {categoryLines.map((line, i) => (
            <p key={i}>{line} · includes 1 county free</p>
          ))}
          <p>Extra counties <span className="font-bold text-zinc-900">{formatPrice(pricePerCounty)}</span>/mo each · save <span className="font-bold text-zinc-900">{discountPercent}%</span> yearly</p>
        </div>
      </div>

      {loading || !settings ? (
        <div className="flex items-center justify-center py-20" data-testid="pricing-loading">
          <div className="h-10 w-10 border-4 border-zinc-200 border-t-zinc-900 rounded-full animate-spin"></div>
        </div>
      ) : (
        <PriceCalculator pricing={pricing} lockedBilling={lockedBilling} />
      )}
    </section>
  );
}

function PriceCalculator({ pricing, lockedBilling }) {
  const { pricePerCounty, discountPercent, salesEmail, maxCounties } = pricing;
  const [billing, setBilling] = useState(lockedBilling || "monthly");
  const [selectedTypes, setSelectedTypes] = useState(["probate"]);
  const [counties, setCounties] = useState(1);

  const toggleType = (key) => {
    setSelectedTypes((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const nTypes = selectedTypes.length;
  const { total, monthlyEquivalent, includedCounties, extraCounties } = computeBundlePrice(
    pricing, selectedTypes, counties, billing
  );
  const suffix = billing === "yearly" ? "/yr" : "/mo";
  const disabled = nTypes === 0 || counties < 1;
  const ctaHref = `/pricing/new?billing=${billing}&lead_types=${selectedTypes.join(",")}&count=${counties}`;

  return (
    <div
      className="max-w-4xl mx-auto rounded-2xl sm:rounded-3xl border-2 border-zinc-200 bg-white p-5 sm:p-8 md:p-10 shadow-sm"
      data-testid="price-calculator"
    >
      <div className="text-center mb-5 sm:mb-6">
        <div className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 rounded-full px-4 py-1.5 mb-3 sm:mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-600">Calculate your price</p>
        </div>
        <h3
          className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Build your plan in real time
        </h3>
        <p className="text-xs sm:text-sm text-zinc-500 mt-2 sm:mt-3">
          Extra counties {formatPrice(pricePerCounty)}/mo · save {discountPercent}% yearly
        </p>
      </div>

      <div className="flex items-center justify-center mb-8" data-testid="calculator-billing-toggle">
        <div className="inline-flex items-center p-1 rounded-full border border-zinc-200 bg-zinc-50">
          {(!lockedBilling || lockedBilling === "monthly") && (
            <button
              type="button"
              data-testid="calculator-billing-monthly"
              onClick={() => setBilling("monthly")}
              className={`px-5 py-2 text-sm font-bold rounded-full transition-colors ${
                billing === "monthly" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"
              }`}
            >
              Monthly
            </button>
          )}
          {(!lockedBilling || lockedBilling === "yearly") && (
            <button
              type="button"
              data-testid="calculator-billing-yearly"
              onClick={() => setBilling("yearly")}
              className={`px-5 py-2 text-sm font-bold rounded-full flex items-center gap-1.5 transition-colors ${
                billing === "yearly" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"
              }`}
            >
              Yearly
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                billing === "yearly" ? "bg-green-400 text-zinc-900" : "bg-green-50 text-green-700 border border-green-200"
              }`}>
                −{discountPercent}%
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">
            Lead Types <span className="text-zinc-400 font-normal">(1 county included each)</span>
          </p>
          <div className="grid grid-cols-2 gap-2" data-testid="calculator-lead-types">
            {PLAN_LEAD_TYPES.map(({ key, label }) => {
              const active = selectedTypes.includes(key);
              const ltPrice = getLeadTypePrice(key, pricing);
              return (
                   <button
                    key={key}
                    type="button"
                    onClick={() => toggleType(key)}
                    className={`flex flex-col items-start sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-3 rounded-lg border-2 text-sm font-semibold transition-colors ${
                      active
                        ? "border-zinc-900 bg-zinc-900 text-white"
                        : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <div
                        className={`h-4 w-4 rounded flex items-center justify-center border-2 flex-shrink-0 ${
                          active ? "border-white bg-white" : "border-zinc-300"
                        }`}
                      >
                        {active && (
                          <Check
                            className="h-2.5 w-2.5 text-zinc-900"
                            strokeWidth={3}
                          />
                        )}
                      </div>

                      {label}
                    </span>

                   

                    <span
                    className={`text-xs font-bold flex-shrink-0 w-full text-center sm:w-auto sm:text-left ${
                      active ? "text-zinc-300" : "text-zinc-400"
                    }`}
                  >
                    {formatPrice(ltPrice)}/mo
                  </span>
                  </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">
            Counties ·{" "}
            <span className="text-zinc-900">
              {nTypes > 0
                ? <>first {nTypes} free, then {formatPrice(pricePerCounty)}/extra</>
                : <>{formatPrice(pricePerCounty)}/extra (pick lead types first)</>}
            </span>
          </p>
          <div
            className="rounded-lg border-2 border-zinc-200 bg-white p-6 flex flex-col items-center justify-center h-[calc(100%-1.75rem)]"
            data-testid="calculator-counties"
          >
            <div className="flex items-center gap-5">
              <button
                type="button"
                data-testid="calculator-county-minus"
                onClick={() => setCounties(Math.max(1, counties - 1))}
                disabled={counties <= 1}
                className="h-10 w-10 rounded-lg border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed text-xl font-bold text-zinc-700"
              >
                −
              </button>
              <span
                className="text-4xl font-black tracking-tight text-zinc-900 tabular-nums w-16 text-center"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid="calculator-county-count"
              >
                {counties}
              </span>
              <button
                type="button"
                data-testid="calculator-county-plus"
                onClick={() => setCounties(Math.min(maxCounties, counties + 1))}
                disabled={counties >= maxCounties}
                className="h-10 w-10 rounded-lg border-2 border-zinc-200 flex items-center justify-center hover:border-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed text-xl font-bold text-zinc-700"
              >
                +
              </button>
            </div>

            {nTypes > 0 && counties <= nTypes && (
              <p className="mt-3 text-xs font-semibold text-green-700" data-testid="calculator-county-included-note">
                {counties} {counties === 1 ? "county is" : "counties are"} included free with your plan
              </p>
            )}
            {nTypes > 0 && counties > nTypes && (
              <p className="mt-3 text-xs font-semibold text-zinc-700" data-testid="calculator-county-extra-note">
                {nTypes} included · {extraCounties} extra × {formatPrice(pricePerCounty)}
              </p>
            )}
            {nTypes === 0 && (
              <p className="text-xs text-zinc-500 mt-3">
                {counties === 1 ? "1 county" : `${counties} counties`}
              </p>
            )}

            {counties >= maxCounties && (
              <p className="mt-3 text-xs font-semibold text-zinc-900">
                Need more?{" "}
                <a
                  href={`mailto:${salesEmail}?subject=Need%20more%20than%20${maxCounties}%20counties`}
                  className="underline underline-offset-2 hover:text-zinc-600"
                  data-testid="calculator-contact-sales"
                >
                  Contact us →
                </a>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 border-t border-zinc-200 pt-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Your total</p>
            <div className="flex items-baseline gap-2">
              <span
                className="text-5xl font-black tracking-tight text-zinc-900 tabular-nums"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid="calculator-total"
              >
                {formatPrice(total)}
              </span>
              <span className="text-lg font-bold text-zinc-500">{suffix}</span>
            </div>
            <p className="text-sm text-zinc-500 mt-2" data-testid="calculator-breakdown">
              {nTypes > 0
                ? (
                  <>
                    {nTypes} lead type{nTypes === 1 ? "" : "s"}
                    {" "}({includedCounties} count{includedCounties === 1 ? "y" : "ies"} included)
                    {extraCounties > 0 && <> + {extraCounties} extra × {formatPrice(pricePerCounty)}</>}
                    {billing === "yearly" && <> &nbsp;· billed yearly at {formatPrice(monthlyEquivalent)}/mo effective</>}
                  </>
                )
                : "Pick at least one lead type to see your price"}
            </p>
          </div>
          <Link to={disabled ? "#" : ctaHref} data-testid="calculator-cta">
            <Button
              className={`h-12 px-8 text-sm font-bold ${disabled ? "opacity-40 cursor-not-allowed" : "bg-zinc-900 hover:bg-zinc-800 text-white"}`}
              disabled={disabled}
            >
              Continue with this plan <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: Phone, title: "Skip-traced & verified", body: "Every contact enriched with phone, email, and mailing address — no guessing, no scrubbing." },
  { icon: MapPin, title: "County-level targeting", body: "Farm only the counties you work. Swap counties on demand, never locked in." },
  { icon: Layers, title: "8 lead types, one platform", body: "FSBO, Probate, Divorce, Tax Liens, NOD, Expired, Canceled, Withdrawn — filtered and live." },
  { icon: Zap, title: "Fresh daily leads", body: "Inventory refreshed continuously and deduplicated before it hits your pipeline." },
  { icon: Download, title: "Export anytime", body: "Download any filtered view as CSV. Drop it straight into your CRM or dialer." },
  { icon: ShieldCheck, title: "Self-serve billing", body: "Subscribe, upgrade, or cancel from your dashboard. No back-and-forth with sales." },
];

function Features() {
  const siteName = useSiteName();
  return (
    <section className="border-y border-zinc-200/50 bg-gradient-to-b from-white to-zinc-50/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20" data-testid="landing-features">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 rounded-full px-4 py-1.5 mb-5 sm:mb-6">
            {/* <Zap className="h-3.5 w-3.5 text-zinc-600" fill="currentColor" /> */}
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-600">Why {siteName}</p>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Built for agents who close.
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="group p-6 border-2 border-zinc-200/80 bg-white rounded-2xl hover:border-zinc-400 hover:shadow-2xl hover:shadow-zinc-200/50 transition-all duration-300 hover:-translate-y-1"
              data-testid={`feature-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              <div className="h-12 w-12 bg-gradient-to-br from-zinc-100 to-zinc-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Icon className="h-6 w-6 text-zinc-900" strokeWidth={2.5} />
              </div>
              <h3
                className="text-lg font-bold tracking-tight text-zinc-900 mb-2"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {title}
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FooterCTA({ supportEmail }) {
  const token = useAuthStore((s) => s.token);
  const siteName = useSiteName();
  return (
    <section className="bg-gradient-to-br from-zinc-900 to-zinc-800 text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-zinc-700 to-transparent rounded-full blur-3xl opacity-20"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-zinc-700 to-transparent rounded-full blur-3xl opacity-20"></div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24 text-center relative">
        <h2
          className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-5 sm:mb-6"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Stop prospecting.<br />
          <span className="bg-gradient-to-r from-white to-zinc-300 bg-clip-text text-transparent">
            Start closing.
          </span>
        </h2>
        
        <p className="text-zinc-400 text-base sm:text-lg max-w-lg mx-auto mb-8 sm:mb-10 leading-relaxed">
          Pick your counties. Pick your lead types. Start working fresh inventory in under two minutes.
        </p>
        
        <Link to={token ? "/subscriptions" : "/signup"} data-testid="landing-footer-cta-button">
          <Button variant="accent" className="h-11 sm:h-12 px-6 sm:px-8 text-sm sm:text-base bg-white hover:bg-zinc-100 text-zinc-900 transition-all duration-200 hover:shadow-2xl hover:shadow-white/20 hover:-translate-y-0.5 rounded-xl font-bold">
            {token ? "Manage subscriptions" : "Start now"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
        
        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-zinc-700/50">
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-zinc-400">
            <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-zinc-600">•</span>
            <a href={`mailto:${supportEmail}`} className="underline underline-offset-2 hover:text-white transition-colors">Contact support</a>
          </div>
          <p className="mt-3 text-sm text-zinc-500" data-testid="landing-copyright">
            © {new Date().getFullYear()} {siteName}. All rights reserved.
          </p>

          {supportEmail && (
        <p className="text-sm text-zinc-500 mt-2">
          Support:{" "}
          <a href={`mailto:${supportEmail}`} className="text-zinc-400 underline underline-offset-2 hover:text-white transition-colors">
            {supportEmail}
          </a>
        </p>
        )}
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const token = useAuthStore((s) => s.token);
  const [lockedBilling, setLockedBilling] = useState(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["public-settings"],
    queryFn: getSettings,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!token) return;
    getBillingPeriod().then((r) => { if (r?.billing) setLockedBilling(r.billing); }).catch(() => {});
  }, [token]);

  return (
    <div className="min-h-screen bg-white text-zinc-950 selection:bg-zinc-900 selection:text-white">
      <Navbar />
      <Hero />
      <StatsSection />
      <Pricing settings={settings} loading={isLoading} lockedBilling={lockedBilling} />
      <Features />
     <FooterCTA supportEmail={settings?.data?.support_email ?? "support@probatedivorce.com"} />
    </div>
  );
}
